import type { OrderStatus } from '../schemas/enums';
import type { Order, OrderCvx } from '../schemas/order/orderSchema';

// Helper methods that don't fit under actions.

type OrderDisplayReferenceParams = {
  localOrderId: number | null | undefined;
  uuid: string | null | undefined;
};

export const isItemPaid = (order: Order, orderItemId: string): boolean => {
  const paymentForItem = order.payments.find((p) => p.orderItemUuids?.includes(orderItemId));
  return !!paymentForItem;
};

/**
 * Returns the customer-facing order reference.
 *
 * Prefer `localOrderId` when available because it is the short in-store number staff already use.
 * Otherwise fall back to the last 4 chars of `uuid`.
 *
 * We intentionally use `uuid` rather than Convex `_id` for the fallback because `uuid` exists for
 * all orders, including offline POS orders before they sync to Convex. `_id` is only assigned after
 * the order is saved server-side, so it cannot be a universal reference across online, in-store,
 * and offline order flows.
 */
export const getOrderDisplayReference = ({
  localOrderId,
  uuid,
}: OrderDisplayReferenceParams): string | null => {
  if (localOrderId && localOrderId > 0) {
    return localOrderId.toString();
  }

  if (uuid) {
    return uuid.slice(-4);
  }

  return null;
};

/**
 * This function should only be used to calculate `order.isClosed` value.
 * @param orderStatus
 */
export const isOrderClosed = (orderStatus: OrderStatus): boolean => {
  return orderStatus === 'CANCELLED' || orderStatus === 'COMPLETED';
};

export const isOrderAwaitingConfirmation = (order: Order | OrderCvx): boolean => {
  const isOnlineOrder =
    order.platform === 'CONSUMER_WEB' ||
    order.platform === 'CONSUMER_IOS' ||
    order.platform === 'CONSUMER_ANDROID';
  return isOnlineOrder && !order.estWaitTime && order.status === 'SUBMITTED';
};

/**
 * `order.discountTotal` only represents order level discounts.
 * This function calculates both order item level discounts AND order level discounts to find the
 * total discounts applied to an order.
 * @param order
 */
export const calcAllDiscounts = (order: Order): number => {
  // Calculate the sum of all the order item discounts
  const orderItemDiscounts = order.orderItems.reduce((sum, item) => sum + item.discountTotal, 0);
  return order.discountTotal + orderItemDiscounts;
};

/**
 * `order.surchargeTotal` only represents order level surcharges.
 * This function calculates both order item level surcharges AND order level surcharges to find the
 * total surcharges applied to an order.
 * @param order
 */
export const calcAllSurcharges = (order: Order): number => {
  // Calculate the sum of all the order item surcharges
  const orderItemSurcharges = order.orderItems.reduce((sum, item) => sum + item.surchargeTotal, 0);
  return order.surchargeTotal + orderItemSurcharges;
};

export const calcDiscountTotal = (subtotal: number, discountPercent: number): number => {
  // If the discount % is 0, or the item price itself was already 0, set discount amount to 0.
  if (discountPercent === 0 || subtotal === 0) {
    return 0;
  }
  // A 100% discount, means a free item. Set the discount equal to item price.
  if (discountPercent === 100) {
    return subtotal;
  }
  const discountAsDecimal = discountPercent / 100;
  return roundToNearest5Cents(Math.round(subtotal * discountAsDecimal));
};

export const calcSurchargeTotal = (subtotal: number, surchargePercent: number): number => {
  // If the discount % is 0, or the item price itself was already 0, set discount amount to 0.
  if (surchargePercent === 0 || subtotal === 0) {
    return 0;
  }
  const surchargeAsDecimal = surchargePercent / 100;
  return roundToNearest5Cents(Math.round(subtotal * surchargeAsDecimal));
};

/**
 * Round a number to the nearest 5 cents.
 * @param cents
 * @example
 *   roundToNearest5(12) // 10
 *   roundToNearest5(13) // 15
 */
export const roundToNearest5Cents = (cents: number) => Math.round(cents / 5) * 5;

/**
 * Check if an order has pending EFTPOS payments via Linkly.
 * Used to prevent order modifications while EFTPOS payments are being processed.
 * @param order
 */
export const hasPendingEftposPayment = (order: Order | OrderCvx): boolean => {
  return order.payments.some(
    (p) => p.status === 'PENDING' && p.paymentType === 'EFTPOS' && p.paymentGateway === 'LINKLY',
  );
};
